import { useEffect, useRef, useState, type JSX } from "react";

/**
 * playline-persistence (Phase 5+ Tier 5E 第 10 段) Phase 5:
 * multi-device sync conflict 検知時 (= 409 PlaylineVersionConflictError 受領) の解決 modal。
 *
 * UX:
 *   - 「別 device で先に進捗があります。 どちらを使いますか?」
 *   - server / client 双方の lastPlayedAt + versionVector + deviceFingerprint を 並列表示
 *   - 「cloud (= 別 device) を使う」: 親 callback の onUseCloud (= cloud 再開、 local 破棄)
 *   - 「local を使う」: 親 callback の onUseLocal (= force overwrite で cloud 上書き)
 *   - Escape close (= 「あとで決める」 と同等、 onClose 呼出)
 *
 * 配置: standalone reusable (= 親 component が sync 結果 409 を catch して trigger)。
 */
export interface ConflictPlaylineSnapshot {
  lastPlayedAt: string | null;
  versionVector: number;
  deviceFingerprint: string | null;
}

export interface ConflictResolutionModalProps {
  playlineId: string;
  cloud: ConflictPlaylineSnapshot;
  local: ConflictPlaylineSnapshot;
  onUseCloud: () => void;
  onUseLocal: () => void;
  onClose: () => void;
}

function formatTs(iso: string | null): string {
  if (iso === null) return "不明";
  try {
    return new Date(iso).toLocaleString("ja-JP");
  } catch {
    return "不明";
  }
}

export function ConflictResolutionModal({
  playlineId,
  cloud,
  local,
  onUseCloud,
  onUseLocal,
  onClose,
}: ConflictResolutionModalProps): JSX.Element {
  const [isResolving, setIsResolving] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const focusable = dialogRef.current?.querySelector<HTMLElement>("button");
    focusable?.focus();
    function handleKey(e: KeyboardEvent): void {
      if (e.key === "Escape" && !isResolving) onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isResolving, onClose]);

  function handleUseCloud(): void {
    setIsResolving(true);
    try {
      onUseCloud();
    } finally {
      setIsResolving(false);
    }
  }

  function handleUseLocal(): void {
    setIsResolving(true);
    try {
      onUseLocal();
    } finally {
      setIsResolving(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="conflict-modal-title"
      data-testid="conflict-resolution-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      ref={dialogRef}
    >
      <div className="w-full max-w-xl rounded-lg bg-white p-6 shadow-xl">
        <h2 id="conflict-modal-title" className="text-lg font-bold mb-2">
          複数の端末で進捗が見つかりました
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          別の端末で先に進めた進捗があるため、 どちらの進捗を使うか選択してください。
          選ばなかった方の進捗は破棄されます。
        </p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <section
            data-testid="conflict-snapshot-cloud"
            className="rounded border border-blue-300 bg-blue-50 p-3 text-sm"
          >
            <h3 className="font-semibold mb-2">クラウド (別端末)</h3>
            <dl className="text-xs space-y-1">
              <div>最終: {formatTs(cloud.lastPlayedAt)}</div>
              <div>version: {cloud.versionVector}</div>
              {cloud.deviceFingerprint !== null && (
                <div>端末: {cloud.deviceFingerprint.slice(0, 12)}…</div>
              )}
            </dl>
          </section>
          <section
            data-testid="conflict-snapshot-local"
            className="rounded border border-amber-300 bg-amber-50 p-3 text-sm"
          >
            <h3 className="font-semibold mb-2">この端末</h3>
            <dl className="text-xs space-y-1">
              <div>最終: {formatTs(local.lastPlayedAt)}</div>
              <div>version: {local.versionVector}</div>
              {local.deviceFingerprint !== null && (
                <div>端末: {local.deviceFingerprint.slice(0, 12)}…</div>
              )}
            </dl>
          </section>
        </div>

        <p className="text-xs text-gray-500 mb-4">
          playline ID: {playlineId.slice(0, 16)}…
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="conflict-cancel"
            onClick={onClose}
            disabled={isResolving}
            className="rounded border border-gray-300 px-4 py-2 text-sm"
          >
            あとで決める
          </button>
          <button
            type="button"
            data-testid="conflict-use-cloud"
            onClick={handleUseCloud}
            disabled={isResolving}
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white"
          >
            クラウドを使う
          </button>
          <button
            type="button"
            data-testid="conflict-use-local"
            onClick={handleUseLocal}
            disabled={isResolving}
            className="rounded bg-amber-600 px-4 py-2 text-sm text-white"
          >
            この端末を使う
          </button>
        </div>
      </div>
    </div>
  );
}
